import { Inject, Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs';
import { delay, mergeMap, tap } from 'rxjs/operators';
import { InitializationProgressComponent } from '../../components/initialization-progress/initialization-progress.component';
import { InitializationInstanceToken, InitializationInterface } from '../../interfaces/initialization.interface';
import moment from 'moment';

@Injectable({
    providedIn: 'root',
})
export class AppInitializationGuard implements CanActivate {
    public constructor(
        @Inject(InitializationInstanceToken) private readonly initializationService: InitializationInterface,
        private readonly modalService: BsModalService,
    ) {}

    public canActivate(): Observable<boolean> {
        if (this.initializationService.isInitialized) {
            return of(true);
        }

        const modalInstance = this.modalService.show(InitializationProgressComponent, {
            backdrop: 'static',
            class: 'modal-md modal-dialog-centered',
            initialState: {
                progress$: this.initializationService.initMessages$,
            },
        });

        const halfSecond = 500;
        const now = moment();
        const modalCloseTime = now.add(halfSecond, 'milliseconds');

        // delayWhen is used to make sure that the loading screen is displayed at least 500 milliseconds before hide is
        // being called.
        // Background: ngx-bootstrap modals have a bug where they will not hide if hide() is called before they are
        //             actually drawn on the screen. That means the modal would stay open indefinitely if the service
        //             took less then the time it takes to render the modal. Since the modal uses an animation to appear
        //             and that animation takes 500 milliseconds, this is the amount of time we are waiting before
        //             trying to attempt to close the modal.
        return (
            modalInstance.content?.rendered$.pipe(
                mergeMap(() => this.initializationService.checkInit()),
                delay(modalCloseTime.toDate()),
                tap((initSuccess) => {
                    if (initSuccess) {
                        modalInstance.hide();
                    }
                }),
            ) ?? of(false)
        );
    }
}
